/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useMemo, useState, useEffect, useContext } from "react";
import { getLocale, useIntl, useSelector } from "umi";
import PerformanceMetricsTable from "@/pages/dashboard/components/PerformanceMetricsTable";
import SelectShowValueType, { ShowValueType } from "@/pages/dashboard/pages/overview/components/OverviewTable/components/SelectShowValueType";
import MetricsSettings, { defaultStatisticsTypeValue } from "@/pages/dashboard/components/MetricsSettings";
import {
  useStatisticsDataFormat,
  useTableColumns,
  useTableDataFormat,
  useMetricsColumns
} from "./hooks";
import { LANGUAGE_MAP, PERIODTIMEOBJ, textLocalConfig } from "@/pages/dashboard/_config/constant";
import DimensionAccumulateRow from "@/pages/dashboard/components/DimensionAccumulateRow";
import useData from "./useData";
import { MetricsSettingsProp, TableProps } from "./types";
import { useLocalText, useSelectArrObj } from "@/pages/dashboard/utils/customHook";
import { FilterContext } from "@/pages/dashboard/components/PageContainer/context";
import { SortBy } from "tea-component/lib/table/addons";
import { PagingQuery } from "tea-component";
import { Option } from "@/pages/dashboard/components/DownloadFormatModal/types";
import { ComponentMetricsItemType } from "@/pages/dashboard/components/PageContainer/type";

import "@/pages/dashboard/assets/scss/page/Overlay/components/tableConext.scss";
import moment from "moment";
import { downloadFileWithFileFormat, useNotificationOfDownloadTable } from "@/pages/dashboard/components/DownloadFormatModal";

import './index.scss';
import { IsSingleDay } from "@/pages/dashboard/utils/utils";

export default function Table(props: TableProps) {
  const { config, style, className } = props || {};
  const locale = getLocale();
  const { formatMessage } = useIntl();
  const contextObj = useContext(FilterContext);
  const { filterVal, filterLoading } = contextObj || {};
  const { selector = {},  filterDefaultSelect, pagePath } = filterVal || {};
  const { explainInfo, gameCode } = useSelector((state: any) => ({
    explainInfo: state.dashboard?.explainInfo,
    gameCode: state?.dashboard?.currentGame?.game_code,
  }));

  const [columns, setColumns] = useState<any[]>([]);
  const [selectType, setSelectType] = useState<ShowValueType>("value_only");
  const [showStatistic, setShowStatistic] = useState<string[]>(defaultStatisticsTypeValue);
  const [accumulated, setAccumulated] = useState<string[]>(['date']);
  let SEPARATED_OPTIONS = useSelectArrObj(filterVal, false, false, filterDefaultSelect, pagePath, true);
  SEPARATED_OPTIONS = SEPARATED_OPTIONS?.filter((i: { groupKey: string; }) => i?.groupKey === 'separated');
  SEPARATED_OPTIONS.unshift({
    groupKey: 'separated',
    value: 'date',
    text: formatMessage({ id: 'common_by_Date' }),
    // disabled: true,
  });

  const compData = useData(filterVal, filterLoading, selectType, config);

  const {
    loading, isError, paging, setPaging, sort, setSort,
    total, tableData, statisticsData, filterValueSort, accumulateOldValueRef,
    getRequestParams, getData, downLoadData, frontLogicalFlag, overLimitBarTriggerKey
  } = compData || {};

  // 是否取消勾选date维度
  const hasDate = accumulateOldValueRef?.current?.includes('date');
  // 是否单日单周单月
  const isSingle = IsSingleDay({ rangeTime: filterVal?.rangeTime, period: PERIODTIMEOBJ[filterVal?.timeType] });
  // 是否累计值计算逻辑
  const isAccumulated = !hasDate && !isSingle;

  // 列定义, 中英文切换(全部), Accumulate关闭(仅仅维度列),
  const metricList = useMemo(() => {
    const metrics = isAccumulated ? filterVal?.metrics?.filter((v: any) => v !== 'total_users')?.map((w: any) => {
      if (w === 'active_users') return 'avg_dau';
      return w;
    }) : filterVal?.metrics || [];
    config.componentMetrics = config?.componentMetrics?.map((item: any) => {
      if (item?.metricKey === 'active_users' || item?.metricKey === 'avg_dau') {
        item = {
          ...item,
          metricKey: isAccumulated ? 'avg_dau' : 'active_users',
          reqMetricKey: isAccumulated ? 'avg_dau' : 'active_users',
          enName: isAccumulated ? 'Avg.DAU' : 'DAU',
          cnName: isAccumulated ? '日平均活跃' : '日活跃',
        };
      }
      return item;
    }) || [];
    return config?.componentMetrics?.filter((v: any) => metrics?.includes(v?.metricKey));
  }, [filterVal, isAccumulated]);

  const COLUMN_CONFIG = useMetricsColumns(metricList || []);

  const [showMetrics, setShowMetrics] = useState(Object.keys(COLUMN_CONFIG));
  useEffect(() => {
    setShowMetrics(Object.keys(COLUMN_CONFIG));
  }, [JSON.stringify(COLUMN_CONFIG)]);

  const { DATE_COLUMN, getAccumulateColumns, getMetricColumns } = useTableColumns(filterVal, hasDate, isSingle);

  useEffect(() => {
    const columns = [
      DATE_COLUMN,
      ...getAccumulateColumns(accumulateOldValueRef?.current?.filter(v => v !== 'date'), gameCode, selector),
      ...getMetricColumns(showMetrics, COLUMN_CONFIG, explainInfo, selectType)
    ];
    setColumns(columns);
  }, [accumulateOldValueRef?.current, selectType, filterVal, showMetrics, locale, metricList]);

  // 表格数据
  const records = [
    ...useTableDataFormat(tableData, COLUMN_CONFIG, filterVal),
    ...useStatisticsDataFormat(statisticsData, COLUMN_CONFIG, filterVal, showStatistic, false)
  ];
  const copyData = [
    ...useTableDataFormat(tableData, COLUMN_CONFIG, filterVal, true),
    ...useStatisticsDataFormat(statisticsData, COLUMN_CONFIG, filterVal, showStatistic, true)
  ];

  // 下载弹窗指标options
  const metricOptions: Option[] = useMemo(() => Object.keys(COLUMN_CONFIG).map((key) => {
    const config = COLUMN_CONFIG[key]?.metricsConfig;
    const name = locale === 'en-US' ? config?.enName : config?.cnName;
    return ({ value: key, text: name });
  }), [COLUMN_CONFIG]);

  const onSortChange = (sorts: SortBy[] = []) => {
    // 排序
    setSort(sorts);
    // 发起请求
    const params = getRequestParams(filterValueSort?.current?.paging, sorts, filterValueSort?.current?.filterValue);
    // forceUpdate()
    getData(params);
  };

  const getExtractMetrics = (configMetrisArr: ComponentMetricsItemType[], metricsArr: any[]) => configMetrisArr.filter(item => metricsArr.includes(item.reqMetricKey));

  const localText = useLocalText(textLocalConfig);
  const { setNoticeStatus } = useNotificationOfDownloadTable(localText?.dateTitle);

  function downPromise() {
    const metricNameArr = getExtractMetrics(config?.componentMetrics || [], showMetrics);
    const metricsMap: { [key: string]: { en: string; zh: string; type: any; }; } = {};
    metricNameArr?.forEach(item => {
      metricsMap[item?.reqMetricKey] = {
        en: item?.enName || '',
        zh: item?.cnName || '',
        type: item?.type
      };
    });
    const params = getRequestParams(paging, sort, filterValueSort?.current?.filterValue, {
      metrics: showMetrics,
      metrics_map: metricsMap,
      show_dod: selectType === "dod",
      show_sdlw: selectType === "sdlw",
      show_change: selectType === "change",
      // file_format: fileFormat,
      aggregates: showStatistic,
      filter_event_date_field: 'start_time',
    });
    const api = `${config?.api?.tableDownloadApi}?language=${LANGUAGE_MAP[locale]}`;
    return downLoadData(api, params);
  }
  // 下载
  function handleDownload() {
    const downloadPromise = downPromise?.();
    if (downloadPromise) { // 有返回Promise对象时由内部处理下载过程
      // 展示正在下载中通知
      setNoticeStatus("loading");
      // 发起请求下载
      (async () => {
        try {
          const res = await downloadPromise;
          const fileName = `${localText?.dateTitle.replace(/\s/g, '_')} ${moment().format('YYYY_MM_DD HH_mm_ss')}`;
          await downloadFileWithFileFormat(res, fileName, {
            onDownloadSuccess() {
              setNoticeStatus("success");
            },
            onDownloadError() {
              setNoticeStatus("error");
            }
          });
        } catch (e) {
          setNoticeStatus("error");
        }
      })();
    }
  }

  return (
    <div style={style || {}} className={className}>
      <PerformanceMetricsTable
        className='dbd-date-event-table'
        title={localText?.dateTitle}
        columns={columns}
        records={records}
        copyConfig={
          {
            // copyTitleList: [],
            copyData
          }
        }
        selectType={selectType}
        timeType={filterVal?.timeType}
        sorting={{
          defaultSorts: sort,
          onSortChange,
        }}
        paging={{
          pageIndex: paging.pageIndex,
          pageSize: paging.pageSize,
          recordCount: total,
          pageSizeOptions: [10, 20, 30, 40, 50, 100, 200],
          onPagingChange(query: PagingQuery) {
            // 切换分页
            setPaging(query);
            // 发起请求
            const params = getRequestParams(query, sort, filterValueSort?.current?.filterValue);
            getData(params);
          }
        }}
        topTip={{
          left: (<div className={`overall-conext-margin js-overview-table-dimension`}>
            <DimensionAccumulateRow
              options={SEPARATED_OPTIONS}
              value={accumulated}
              onChange={(value: string[]) => {
                setAccumulated(value);
                setPaging({ pageSize: 10, pageIndex: 1 });
                setSort([]);
                if (accumulateOldValueRef?.current?.toString() !== value?.toString()) {
                  accumulateOldValueRef.current = value;
                  const params = getRequestParams({ pageSize: 10, pageIndex: 1 }, [], filterValueSort?.current?.filterValue);
                  getData(params);
                }
              }}
            />
            <SelectShowValueType
              timeType={filterVal.timeType}
              value={selectType}
              onChange={(value) => {
                setSelectType(value as ShowValueType);
              }}
              isShowDod
            />
          </div>),
          right: (<MetricsSettings
            key={JSON.stringify(metricOptions)}
            onConfirm={({ metricValues, statisticsType }: MetricsSettingsProp) => {
              setShowStatistic(statisticsType);
              setShowMetrics(metricValues);
            }}
            // disabledOptions={[{ value: "date", text: useIntl().formatMessage({ id: "common_date" }) }]}
            metricOptions={metricOptions}
          />)
        }}
        onResizeEnd={(columns) => setColumns(columns)}
        isShowProgressBar
        loading={loading}
        isError={isError}
        onDownload={handleDownload}
        frontLogicalFlag={frontLogicalFlag}
        limitCount={config?.tableConfig?.overLimitCount}
        barLimitCount={config?.tableConfig?.overLimitBarCount}
        barDownloadDisabled={config?.tableConfig?.barDownloadDisabled}
        overLimitBarTriggerKey={overLimitBarTriggerKey}
      />
    </div>
  );
}
